import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DeleteIcon, EllipsisIcon, SmallEditIcon } from 'secondstep-components'
import HideIcon from './HideIcon'
import AddInstructorIcon from './AddInstructorIcon'
import LeaveClassIcon from './LeaveClassIcon'
import {
  AddInstructorButton,
  ClassCardMenuButton,
  DeleteButton,
  DropdownMenuWrapper,
  EditButton,
  HideButton,
  LeaveClassButton,
  MyClassesMenuButton,
  Wrapper,
} from './component.styles'

const DropdownMenu = ({
  className,
  courseInstanceName,
  dataTestId,
  handleAddInstructorSubmit,
  handleDeleteSubmit,
  handleEditSubmit,
  handleHideSubmit,
  handleLeaveClassSubmit,
  isClassCardMenu,
  isOwner = true,
  canAddInstructor = false,
  forwardedRef,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleMouseDown = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const CardMenuButton = isClassCardMenu
    ? ClassCardMenuButton
    : MyClassesMenuButton

  const showAddInstructor =
    isOwner &&
    canAddInstructor &&
    typeof handleAddInstructorSubmit === 'function'
  const showDelete = isOwner && typeof handleDeleteSubmit === 'function'
  const showLeaveClass =
    !isOwner && typeof handleLeaveClassSubmit === 'function'

  return (
    <Wrapper className={className} ref={menuRef}>
      <CardMenuButton
        aria-label="Class Card Menu Button"
        className="dropdown-menu-button"
        dataTestId={dataTestId}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        ref={forwardedRef}
      >
        <EllipsisIcon />
      </CardMenuButton>
      {isOpen && (
        <DropdownMenuWrapper className="dropdown-menu-wrapper">
          {showAddInstructor && (
            <AddInstructorButton
              aria-label="Class Card Add Instructor Button"
              className="add-instructor-button"
              dataTestId="add-instructor-button"
              onClick={() => {
                setIsOpen(false)
                handleAddInstructorSubmit()
              }}
            >
              <AddInstructorIcon />
              <p>Add Instructor</p>
            </AddInstructorButton>
          )}
          {showLeaveClass && (
            <LeaveClassButton
              aria-label="Class Card Leave Class Button"
              className="leave-class-button"
              dataTestId="leave-class-button"
              onClick={() => {
                setIsOpen(false)
                handleLeaveClassSubmit()
              }}
            >
              <LeaveClassIcon />
              <p>Leave Class</p>
            </LeaveClassButton>
          )}
          <EditButton
            aria-label="Class Card Edit Button"
            className="edit-button"
            dataTestId="edit-button"
            onClick={() => handleEditSubmit()}
          >
            <SmallEditIcon />
            <p>Edit</p>
          </EditButton>
          {handleHideSubmit && (
            <HideButton
              aria-label="Class Card Hide Button"
              className="hide-button"
              dataTestId="hide-button"
              onClick={() => handleHideSubmit()}
            >
              <HideIcon style={{ transform: 'scale(0.75)' }} />
              <p>Hide</p>
            </HideButton>
          )}
          {showDelete && (
            <DeleteButton
              aria-label="Class Card Delete Button"
              className="delete-button"
              dataTestId="delete-button"
              onClick={() => handleDeleteSubmit()}
            >
              <DeleteIcon />
              <p>Delete</p>
            </DeleteButton>
          )}
        </DropdownMenuWrapper>
      )}
    </Wrapper>
  )
}
DropdownMenu.propTypes = {
  canAddInstructor: PropTypes.bool,
  className: PropTypes.string,
  courseInstanceName: PropTypes.string,
  dataTestId: PropTypes.string,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  handleAddInstructorSubmit: PropTypes.func,
  handleDeleteSubmit: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleHideSubmit: PropTypes.func,
  handleLeaveClassSubmit: PropTypes.func,
  isClassCardMenu: PropTypes.bool,
  isOwner: PropTypes.bool,
}

export default DropdownMenu
